import React, { useEffect, useState } from "react";
import { Modal, Spin, Alert } from "antd";
import QRCode from "qrcode";
import QRCodeImage from "@/asserts/images/supplier/qr-code-bg.png";
import { useWecomQRCode } from "../hooks/useWecomQRCode";
import styles from "./QRCodeApprovalModal.module.less";

// ==================== 类型定义 ====================
interface QRCodeApprovalModalProps {
  visible: boolean;
  onClose: () => void;
  loading?: boolean;
  onWecomParamsLoaded?: (params: any) => void;
  businessId?: string | number;
}

// ==================== 主组件 ====================
const QRCodeApprovalModal: React.FC<QRCodeApprovalModalProps> = ({
  visible,
  onClose,
  loading: externalLoading = false,
  onWecomParamsLoaded,
  businessId,
}) => {
  const { wecomParams, loading, error, generateQRCode, clearQRCode } =
    useWecomQRCode();

  const [qrcodeUrl, setQrcodeUrl] = useState<string>("");

  // ==================== 事件处理 ====================
  const handleClose = () => {
    clearQRCode();
    onClose();
  };

  // ==================== 初始化数据 ====================
  useEffect(() => {
    if (!visible) {
      return;
    }

    const initData = async () => {
      // 再获取企业微信参数
      await generateQRCode();
    };

    initData();
  }, [visible, generateQRCode]);

  // ==================== 企业微信参数加载完成回调 ====================
  useEffect(() => {
    if (wecomParams) {
      onWecomParamsLoaded?.(wecomParams);

      // 构建完整的重定向 URI（拼接评审意见页面路径）
      // 注意：企业微信会在 URL 后添加 ?code=xxx&state=xxx
      // 为了确保参数能被正确接收，我们提供两种构建方式：

      // 方式1：Hash 路由（当前使用）
      const fullRedirectUri = `${wecomParams.redirectUri}/#/supplier/assessment-opinion`;

      // 方式2：如果方式1不工作，可以尝试不带 Hash 的路径（需要后端配置支持）
      // const fullRedirectUri = `${wecomParams.redirectUri}/supplier/assessment-opinion`;

      // 将 businessId 编码到 state 参数中
      const stateData = {
        originalState: wecomParams.state,
        businessId: businessId,
      };
      const encodedState = encodeURIComponent(JSON.stringify(stateData));

      // 构建企业微信授权链接
      const authUrl = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${wecomParams.corpId}&redirect_uri=${encodeURIComponent(fullRedirectUri)}&response_type=code&scope=snsapi_base&state=${encodedState}#wechat_redirect`;

      // 使用 qrcode 库生成二维码
      QRCode.toDataURL(authUrl, {
        errorCorrectionLevel: "H",
        type: "image/png",
        width: 300,
        margin: 1,
        color: { dark: "#000000", light: "#ffffff" },
      })
        .then((url) => {
          setQrcodeUrl(url);
        })
        .catch((err) => {});
    }
  }, [wecomParams, businessId, onWecomParamsLoaded]);

  // ==================== 注意 ====================
  // 扫码成功后，企业微信会重定向到 /supplier/assessment-opinion 页面
  // 此时当前页面会被卸载，Modal 组件销毁，无法在这里监听扫码结果
  // 扫码参数的接收和处理在 AssessmentOpinionPage 组件中完成

  const isLoading = loading || externalLoading;

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      width="auto"
      centered
      classNames={{
        body: styles.modalBody,
      }}
      styles={{
        body: { padding: 0, backgroundColor: "transparent" },
        content: { backgroundColor: "transparent", boxShadow: "none" },
        mask: {
          backdropFilter: "blur(4px)",
          backgroundColor: "rgba(0, 0, 0, 0.45)",
        },
      }}
    >
      <div className={styles.qrcodeContainer}>
        {/* 加载状态 */}
        {isLoading && (
          <div className={styles.loadingOverlay}>
            <Spin size="large" tip="正在生成二维码..." />
          </div>
        )}

        {/* 错误提示 */}
        {error && (
          <Alert
            message="获取企业微信参数失败"
            description={error}
            type="error"
            showIcon
            closable
            style={{ marginBottom: 16 }}
          />
        )}

        {/* 二维码容器 */}
        <div
          className={styles.qrcodeWrapper}
          style={{ opacity: isLoading ? 0.5 : 1 }}
        >
          <div
            className={styles.qrcodeImage}
            style={{ backgroundImage: `url(${QRCodeImage})` }}
          >
            <div className={styles.qrcodeImageDynamic}>
              <div className={styles.qrcodeTitle}>二维码审批</div>
              {qrcodeUrl ? (
                // 动态生成的二维码
                <img src={qrcodeUrl} alt="企业微信二维码" />
              ) : (
                // 未生成二维码时的提示
                <div className={styles.processInfo}>
                  <span className={styles.processCode}>
                    {isLoading
                      ? "正在加载企业微信参数..."
                      : wecomParams
                        ? "正在生成二维码，请稍候..."
                        : "点击刷新获取二维码"}
                  </span>
                </div>
              )}
              <div className={styles.qrcodeRemark}>
                企业微信扫码签名30分钟内有效
              </div>
            </div>
          </div>
        </div>

        {/* 操作按钮 */}
        {/* <div className={styles.actionButtons}>
          <Space>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              disabled={isLoading}
              loading={loading}
            >
              刷新二维码
            </Button>
            <Button onClick={handleClose}>关闭</Button>
          </Space>
        </div> */}
      </div>
    </Modal>
  );
};

export default QRCodeApprovalModal;
